import { ExternalLink, LocateFixed, MessagesSquare } from "lucide-react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Callout } from "@/components/ui/feedback";
import { cn, formatDateTime } from "@/lib/utils";
import {
  RUN_STATUS_TEXT, onBoard, trailLines, versionBody,
  type TrailLine, type WorkflowCatalog, type WorkflowDetail, type WorkflowRun, type WorkflowRunStatus,
} from "@/lib/workflows";
import { useWorkflowVersion } from "@/lib/workflows-api";

const TONE: Record<WorkflowRunStatus, "accent" | "success" | "neutral" | "danger"> = {
  active: "accent",
  completed: "success",
  exited: "neutral",
  failed: "danger",
};

const DOT: Record<TrailLine["tone"], string> = {
  neutral: "bg-border-strong",
  success: "bg-success-9",
  danger: "bg-danger-9",
  accent: "bg-accent-9",
};

/**
 * One lead's trail through a workflow (ACL-406), opened from Enrollment history: every step it
 * passed, oldest first, with its time and what happened there (`trailLines`), then the box it is on
 * now and when it moves on, or why it finished. Boxes are named from the version the lead is on:
 * the live one comes with the workflow, an older one is read once (`useWorkflowVersion`). A sent
 * text opens the lead's conversation. "Show on board" opens the builder at the lead's box, which
 * the builder can only find while the draft still has it.
 */
export function WorkflowTrail({
  run, workflow, catalog, onClose, onShowOnBoard,
}: {
  run: WorkflowRun | null;
  workflow: WorkflowDetail;
  catalog: WorkflowCatalog | undefined;
  onClose: () => void;
  onShowOnBoard: (stepId: string) => void;
}) {
  const older = run !== null && run.version_n !== null && run.version_n !== workflow.published_version_n;
  const version = useWorkflowVersion(workflow.id, run?.version_n ?? null, older);
  const body = run ? versionBody(run, workflow, version.data?.body) : null;
  const lines = run ? trailLines(run, body, catalog) : [];
  const now = lines.find((l) => l.key === "now");
  const showable = onBoard(now?.stepId, workflow.draft_body);
  const name = run ? (run.lead_deleted ? "Deleted contact" : (run.lead_name ?? "Unnamed lead")) : "";

  return (
    <Dialog open={run !== null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent variant="drawer">
        {run && (
          <>
            <DialogHeader>
              <DialogTitle>{name}</DialogTitle>
              <DialogDescription className="flex flex-wrap items-center gap-2">
                <span>{workflow.name}</span>
                <Badge tone={TONE[run.status]} size="sm">
                  {RUN_STATUS_TEXT[run.status]}
                </Badge>
                {run.version_n !== null && <span className="text-xs">version {run.version_n}</span>}
              </DialogDescription>
            </DialogHeader>
            <DialogBody className="space-y-5">
              {older && version.isError && (
                <Callout tone="warning">Version {run.version_n} could not be read, so the boxes are named by their kind.</Callout>
              )}
              <ol aria-label="The lead's trail" className="space-y-0">
                {lines.map((line, i) => (
                  <li key={line.key} className="relative flex gap-3 pb-4 last:pb-0">
                    {i < lines.length - 1 && <span aria-hidden className="absolute left-[5px] top-4 h-full w-px bg-border" />}
                    <span aria-hidden className={cn("relative mt-1.5 size-[11px] shrink-0 rounded-full ring-2 ring-surface", DOT[line.tone])} />
                    <div className="min-w-0 flex-1">
                      <p className="text-xs text-text-muted">{line.at ? formatDateTime(line.at) : <span className="font-semibold text-accent-11">Now</span>}</p>
                      <p className="text-sm font-medium text-text">{line.label}</p>
                      <p className={cn("text-xs", line.tone === "danger" ? "text-danger-11" : "text-text-muted")}>
                        {line.movesAt && new Date(line.movesAt).getTime() <= Date.now()
                          ? "Due now"
                          : `${line.detail}${line.movesAt ? ` ${formatDateTime(line.movesAt)}` : ""}`}
                      </p>
                      {line.text && !run.lead_deleted && (
                        <Link
                          to={`/conversations/team?lead=${run.lead_id}${line.email ? "&channel=email" : ""}`}
                          className="mt-0.5 inline-flex items-center gap-1 text-xs font-medium text-accent-11 underline-offset-4 hover:underline"
                        >
                          <MessagesSquare className="size-3" aria-hidden />
                          {line.email ? "Open the email" : "Open the text"}
                        </Link>
                      )}
                    </div>
                  </li>
                ))}
              </ol>
              {now && older && (
                <p className="text-xs text-text-muted">
                  This lead is on version {run.version_n}; the board shows the draft, which may have changed since.
                </p>
              )}
              {now && !showable && <p className="text-xs text-text-muted">This box is no longer in the draft, so it can't be shown on the board.</p>}
            </DialogBody>
            <DialogFooter className="justify-between">
              {!run.lead_deleted ? (
                <Button asChild variant="ghost">
                  <Link to={`/contacts/${run.lead_id}`}>
                    <ExternalLink />
                    Open contact
                  </Link>
                </Button>
              ) : (
                <span />
              )}
              {now?.stepId && (
                <Button disabled={!showable} onClick={() => now.stepId && onShowOnBoard(now.stepId)}>
                  <LocateFixed />
                  Show on board
                </Button>
              )}
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
